const MIYABI_POINT_ATTR = {
  discountCode: 'miyabi_point_discount_code',
  appliedPoints: 'miyabi_applied_points',
  expiresAt: 'miyabi_point_expires_at',
  cartKey: 'miyabi_points_cart_key',
  legacyApplied: '利用ポイント',
};

// 旧形式 MIYABI-PT-{customerId}-... と新形式の匿名化コードの両方に一致させる
const MIYABI_DISCOUNT_CODE_PATTERN = /^MIYABI-PT/i;

class CartPoints extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.cartTotal = Number(this.dataset.cartTotal) || 0;
    this.itemsSubtotal = Number(this.dataset.itemsSubtotal) || this.cartTotal;
    this.itemCount = Number(this.dataset.itemCount) || 0;
    this.availablePoints = 0;
    this.balanceReady = false;
    this.isApplying = false;
    this.isLeaving = false;
    this.suppressCartInvalidate = false;

    this.appliedPoints = this.sanitizePoints(this.dataset.appliedPoints) || 0;
    this.discountCode = (this.dataset.discountCode || '').trim();
    this.expiresAt = (this.dataset.expiresAt || '').trim();
    this.savedCartKey = (this.dataset.cartKey || '').trim();
    this.needsReapply = false;
    this.reapplyMessage = '';

    this.balanceElement = this.querySelector('[data-points-balance]');
    this.limitElement = this.querySelector('[data-points-limit]');
    this.input = this.querySelector('[data-points-input]');
    this.applyButton = this.querySelector('[data-points-apply]');
    this.attributeInput = this.querySelector('[data-points-attribute]');
    this.discountCodeInput = this.querySelector('[data-points-discount-code]');
    this.expiresAtInput = this.querySelector('[data-points-expires-at]');
    this.cartKeyInput = this.querySelector('[data-points-cart-key]');
    this.statusElement = this.querySelector('[data-points-status]');
    this.discountElement = this.querySelector('[data-points-discount]');
    this.paymentElement = this.querySelector('[data-points-payment]');

    this.onApplyClick = () => this.applyPoints();
    this.onCheckoutClick = (event) => this.handleCheckoutClick(event);
    this.onCartFormSubmit = (event) => this.handleCartFormSubmit(event);

    this.setInputsEnabled(false);
    this.setStatus('ポイントを確認しています…');
    this.updateLimitDisplay();
    this.renderAppliedPoints();
    this.bindCheckoutControls();
    this.updateAdditionalCheckoutVisibility();

    this.applyButton.addEventListener('click', this.onApplyClick);
    this.input.addEventListener('input', () => this.onInputChange());
    this.input.addEventListener('blur', () => this.correctInputIfOverLimit());
    this.input.addEventListener('keydown', (event) => {
      if (['e', 'E', '+', '-', '.', ','].includes(event.key)) {
        event.preventDefault();
      }
    });

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      this.onCartUpdate(event);
    });

    this.bootstrap();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
      this.cartUpdateUnsubscriber = undefined;
    }

    this.unbindCheckoutControls();
    this.applyButton?.removeEventListener('click', this.onApplyClick);
  }

  async bootstrap() {
    try {
      if (window.MiyabiPointsSession?.ready) {
        await window.MiyabiPointsSession.ready;
      }
      const cart = await this.fetchCart();
      this.syncCartSnapshot(cart);
      await this.restoreAppliedState(cart);
    } catch (error) {
      console.warn('Failed to restore cart points state.', error);
    }

    await this.loadBalance();
  }

  isMiyabiDiscountCode(code) {
    return MIYABI_DISCOUNT_CODE_PATTERN.test(String(code || '').trim());
  }

  /**
   * /cart.js は discount_codes を返さないため、
   * cart_level_discount_applications の discount_code 型から適用中コードを取り出す。
   */
  getAppliedDiscountCodes(cart) {
    const applications = Array.isArray(cart?.cart_level_discount_applications)
      ? cart.cart_level_discount_applications
      : [];

    const codes = applications
      .filter((application) => !application.type || application.type === 'discount_code')
      .map((application) => String(application.title || '').trim())
      .filter(Boolean);

    return [...new Set(codes)];
  }

  /**
   * Shopifyカートの割引コードを、保持すべきコードだけに揃える。
   * ポイント以外のコードは残し、不要なMIYABIポイント割引だけを実際に外す。
   */
  async syncShopifyDiscounts(cart, { keepCode = '' } = {}) {
    const appliedCodes = this.getAppliedDiscountCodes(cart);
    const staleCodes = appliedCodes.filter(
      (code) => this.isMiyabiDiscountCode(code) && code !== keepCode
    );

    if (staleCodes.length === 0) return cart;

    const remainingCodes = appliedCodes.filter((code) => !staleCodes.includes(code));
    const updatedCart = await this.updateShopifyDiscounts(remainingCodes);

    console.info('[MIYABI points] removed stale discount codes', staleCodes);

    if (updatedCart) {
      this.syncCartSnapshot(updatedCart);
      return updatedCart;
    }

    return cart;
  }

  async updateShopifyDiscounts(codes) {
    this.suppressCartInvalidate = true;

    try {
      const payload = { discount: codes.join(',') };
      const noteField =
        document.getElementById('Cart-note') || document.getElementById('CartDrawer-Note');
      if (noteField) {
        payload.note = noteField.value;
      }

      const response = await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Cart discount update returned ${response.status}`);
      }

      return response.json();
    } finally {
      this.suppressCartInvalidate = false;
    }
  }

  async clearShopifyPointDiscounts() {
    try {
      const cart = await this.fetchCart();
      await this.syncShopifyDiscounts(cart, { keepCode: '' });
      this.renderAppliedPoints();
    } catch (error) {
      console.warn('Failed to remove point discount from the cart.', error);
    }
  }

  bindCheckoutControls() {
    this.checkoutButton = document.getElementById('checkout');
    this.cartForm = document.getElementById('cart');

    if (this.checkoutButton) {
      this.checkoutButton.addEventListener('click', this.onCheckoutClick);
    }

    if (this.cartForm) {
      this.cartForm.addEventListener('submit', this.onCartFormSubmit);
    }
  }

  unbindCheckoutControls() {
    if (this.checkoutButton) {
      this.checkoutButton.removeEventListener('click', this.onCheckoutClick);
    }

    if (this.cartForm) {
      this.cartForm.removeEventListener('submit', this.onCartFormSubmit);
    }
  }

  handleCheckoutClick(event) {
    if (this.checkoutButton?.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    this.goToCheckout();
  }

  handleCartFormSubmit(event) {
    const submitter = event.submitter;
    if (submitter && submitter.getAttribute('name') === 'checkout') {
      if (submitter.disabled) return;
      event.preventDefault();
      this.goToCheckout();
    }
  }

  async goToCheckout() {
    if (this.isApplying || this.isLeaving) return;
    this.isLeaving = true;

    if (this.checkoutButton) {
      this.checkoutButton.setAttribute('aria-disabled', 'true');
      this.checkoutButton.classList.add('loading');
    }

    try {
      // 入力直後の遷移でも取りこぼさないよう、チェックアウト前に cart note を確定保存する
      if (typeof window.saveCartNoteBeforeCheckout === 'function') {
        await window.saveCartNoteBeforeCheckout();
      } else {
        await this.saveCartNote();
      }

      if (this.hasValidDiscount()) {
        const encodedCode = encodeURIComponent(this.discountCode);
        const redirect = encodeURIComponent('/checkout');
        window.location.href = `/discount/${encodedCode}?redirect=${redirect}`;
        return;
      }

      // ポイント未使用のときは、古いポイント割引を外してから通常のチェックアウトへ進む
      try {
        await this.clearShopifyPointDiscounts();
      } catch (error) {
        console.warn('Failed to remove point discount before checkout.', error);
      }

      window.location.href = '/checkout';
    } catch (error) {
      console.warn('Failed to prepare checkout.', error);
      this.isLeaving = false;
      if (this.checkoutButton) {
        this.checkoutButton.removeAttribute('aria-disabled');
        this.checkoutButton.classList.remove('loading');
      }
      const errors = document.getElementById('cart-errors');
      if (errors) {
        errors.textContent = 'ご要望の保存に失敗しました。もう一度「購入手続きに進む」を押してください。';
      }
    }
  }

  async saveCartNote() {
    const noteField =
      document.getElementById('Cart-note') || document.getElementById('CartDrawer-Note');
    if (!noteField) return;

    const response = await fetch(routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify({ note: noteField.value }),
    });

    if (!response.ok) {
      throw new Error(`Cart note update returned ${response.status}`);
    }
  }

  hasValidDiscount() {
    return (
      this.appliedPoints > 0 &&
      Boolean(this.discountCode) &&
      !this.isExpired(this.expiresAt)
    );
  }

  isExpired(expiresAt) {
    if (!expiresAt) return true;
    const expiresMs = Date.parse(expiresAt);
    if (!Number.isFinite(expiresMs)) return true;
    return expiresMs <= Date.now();
  }

  /**
   * カート内容の指紋。ポイント割引の適用有無で total_price が動くため、
   * 割引の影響を受けない値（点数・商品小計・各行のバリエーションと数量）だけで構成する。
   */
  buildCartKey(cart) {
    const lines = Array.isArray(cart?.items)
      ? cart.items.map((item) => `${item.variant_id || item.id}:${item.quantity}`).join(',')
      : '';

    return [Number(cart?.item_count) || 0, Number(cart?.items_subtotal_price) || 0, lines].join('|');
  }

  /**
   * Shopify Ajax Cart API の金額は通貨の最小単位。
   * 本テーマは Liquid でも | divided_by: 100 しており、JPY でも ×100（例: ¥4800 → 480000）で返る。
   * cartSubtotal は円単位整数。二重に割らないよう、ここで一度だけ変換する。
   */
  moneyToYen(amount) {
    const value = Number(amount);
    if (!Number.isFinite(value) || value < 0) return 0;
    return Math.floor(value / 100);
  }

  syncCartSnapshot(cart) {
    this.cartTotal = Math.max(0, Number(cart.total_price) || 0);
    this.itemsSubtotal = Math.max(0, Number(cart.items_subtotal_price) || this.cartTotal);
    this.itemCount = Math.max(0, Number(cart.item_count) || 0);
    this.cartToken = cart.token || '';
    this.updateLimitDisplay();
  }

  async fetchCart() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Cart.js returned ${response.status}`);
    }

    const cart = await response.json();

    if (!this._loggedCartSample) {
      this._loggedCartSample = true;
      console.info('[MIYABI points] /cart.js sample', {
        token: cart.token,
        items_subtotal_price: cart.items_subtotal_price,
        total_price: cart.total_price,
        item_count: cart.item_count,
        cartSubtotalYen: this.moneyToYen(cart.items_subtotal_price),
      });
    }

    return cart;
  }

  async restoreAppliedState(cart) {
    const attrs = cart.attributes || {};
    const appliedPoints =
      this.sanitizePoints(attrs[MIYABI_POINT_ATTR.appliedPoints]) ??
      this.sanitizePoints(attrs[MIYABI_POINT_ATTR.legacyApplied]) ??
      0;
    const discountCode = String(attrs[MIYABI_POINT_ATTR.discountCode] || '').trim();
    const expiresAt = String(attrs[MIYABI_POINT_ATTR.expiresAt] || '').trim();
    const savedCartKey = String(attrs[MIYABI_POINT_ATTR.cartKey] || '').trim();
    const currentCartKey = this.buildCartKey(cart);

    // 属性にポイント情報が無い場合でも、Shopify側に残ったポイント割引は解除する
    if (appliedPoints <= 0 || !discountCode) {
      this.clearLocalAppliedState();
      await this.syncShopifyDiscounts(cart, { keepCode: '' });
      this.renderAppliedPoints();
      this.updateAdditionalCheckoutVisibility();
      return;
    }

    if (this.isExpired(expiresAt) || savedCartKey !== currentCartKey) {
      await this.invalidateAppliedPoints({
        message: savedCartKey && savedCartKey !== currentCartKey
          ? 'カート内容が変更されたため、ポイントを再適用してください'
          : 'ポイント適用の有効期限が切れたため、再適用してください',
        clearAttributes: true,
        cart,
      });
      return;
    }

    this.appliedPoints = appliedPoints;
    this.discountCode = discountCode;
    this.expiresAt = expiresAt;
    this.savedCartKey = savedCartKey;
    this.input.value = String(appliedPoints);

    // 有効なコード以外のポイント割引（旧コードなど）は外す
    await this.syncShopifyDiscounts(cart, { keepCode: discountCode });

    this.renderAppliedPoints();
    this.updateAdditionalCheckoutVisibility();
  }

  async onCartUpdate(event) {
    if (this.suppressCartInvalidate) return;
    if (event?.source === 'cart-points') return;

    const nextTotal = Number(event?.cartData?.total_price);
    const nextItemsSubtotal = Number(event?.cartData?.items_subtotal_price);
    const nextItemCount = Number(event?.cartData?.item_count);

    if (Number.isFinite(nextTotal)) this.cartTotal = Math.max(0, nextTotal);
    if (Number.isFinite(nextItemsSubtotal)) this.itemsSubtotal = Math.max(0, nextItemsSubtotal);
    if (Number.isFinite(nextItemCount)) this.itemCount = Math.max(0, nextItemCount);

    this.updateLimitDisplay();

    if (this.appliedPoints > 0 || this.discountCode) {
      await this.invalidateAppliedPoints({
        message: 'カート内容が変更されたため、ポイントを再適用してください',
        clearAttributes: true,
        keepInputValue: true,
      });
      return;
    }

    const clamped = this.clampAppliedPoints();
    this.renderAppliedPoints();

    if (clamped && this.balanceReady) {
      this.setStatus(
        `利用できる上限は${this.formatNumber(this.getUsableLimit())}ptです。上限値に修正しました。`
      );
    }
  }

  async invalidateAppliedPoints({
    message = '',
    clearAttributes = true,
    keepInputValue = false,
    cart = null,
  } = {}) {
    const previousInput = this.input.value;
    this.clearLocalAppliedState();

    if (!keepInputValue) {
      this.input.value = '0';
    } else if (previousInput !== '') {
      this.input.value = previousInput;
    }

    this.renderAppliedPoints();
    this.updateAdditionalCheckoutVisibility();

    if (message) {
      this.needsReapply = message.includes('再適用');
      this.reapplyMessage = this.needsReapply ? message : '';
      this.setStatus(message);
    }

    if (clearAttributes) {
      try {
        await this.savePointAttributes({
          appliedPoints: '',
          discountCode: '',
          expiresAt: '',
          cartKey: '',
        });
      } catch (error) {
        console.warn('Failed to clear point attributes.', error);
      }
    }

    try {
      if (cart) {
        await this.syncShopifyDiscounts(cart, { keepCode: '' });
      } else {
        await this.clearShopifyPointDiscounts();
      }
    } catch (error) {
      console.warn('Failed to remove point discount from the cart.', error);
    }

    this.renderAppliedPoints();
  }

  clearLocalAppliedState() {
    this.appliedPoints = 0;
    this.discountCode = '';
    this.expiresAt = '';
    this.savedCartKey = '';
  }

  async loadBalance() {
    this.setInputsEnabled(false);
    if (!this.needsReapply) {
      this.setStatus('ポイントを確認しています…');
    }

    try {
      if (!window.MiyabiPoints || typeof window.MiyabiPoints.getBalance !== 'function') {
        throw new Error('MiyabiPoints helper is not available');
      }

      const result = await window.MiyabiPoints.getBalance();

      if (result.status === 'unauthorized') {
        this.handleUnauthorized(result.message);
        return;
      }

      if (result.status !== 'ok') {
        throw new Error(result.message || 'Points API failed');
      }

      this.availablePoints = window.MiyabiPoints.normalizeBalance(result.pointBalance);
      this.balanceReady = true;
      this.balanceElement.textContent = `${this.formatNumber(this.availablePoints)}pt`;
      this.updateLimitDisplay();

      const clamped = this.clampAppliedPoints();
      this.renderAppliedPoints();
      this.setInputsEnabled(true);

      if (this.needsReapply) {
        this.setStatus(
          this.reapplyMessage || 'カート内容が変更されたため、ポイントを再適用してください'
        );
      } else if (this.hasValidDiscount()) {
        this.setStatus('ポイントを適用しました');
      } else if (clamped) {
        this.setStatus(
          `利用できる上限は${this.formatNumber(this.getUsableLimit())}ptです。上限値に修正しました。`
        );
      } else {
        this.setStatus('');
      }
    } catch (error) {
      this.handleFetchError();
      console.warn('Failed to load points balance.', error);
    }
  }

  handleUnauthorized(message) {
    this.availablePoints = 0;
    this.balanceReady = false;
    this.balanceElement.textContent = '—';
    this.updateLimitDisplay();
    this.clampAppliedPoints();
    this.renderAppliedPoints();
    this.setInputsEnabled(false);
    this.setStatus(message || 'ログインするとポイントを利用できます');
  }

  handleFetchError() {
    this.availablePoints = 0;
    this.balanceReady = false;
    this.balanceElement.textContent = '—';
    this.updateLimitDisplay();
    this.clampAppliedPoints();
    this.renderAppliedPoints();
    this.setInputsEnabled(false);
    this.setStatus('ポイントを取得できませんでした');
  }

  getSubtotalYen() {
    // 送料行を含まない商品小計（送料込価格の商品合計）を円単位で返す
    return this.moneyToYen(this.itemsSubtotal);
  }

  getMaxBySubtotal() {
    return Math.floor(this.getSubtotalYen() * 0.1);
  }

  getUsableLimit() {
    if (!this.balanceReady) return this.getMaxBySubtotal();
    return Math.min(this.availablePoints, this.getMaxBySubtotal());
  }

  updateLimitDisplay() {
    const usableLimit = this.balanceReady ? this.getUsableLimit() : this.getMaxBySubtotal();
    if (this.limitElement) {
      this.limitElement.textContent = this.balanceReady
        ? `今回利用できる上限：${this.formatNumber(usableLimit)}pt`
        : '今回利用できる上限：—';
    }
    this.input.max = String(Math.max(0, usableLimit));
  }

  setInputsEnabled(enabled) {
    this.input.disabled = !enabled;
    this.applyButton.disabled = !enabled || this.isApplying;
  }

  setStatus(message, { isError = false } = {}) {
    this.statusElement.textContent = message || '';
    this.statusElement.classList.toggle('cart-points__status--error', Boolean(isError && message));
  }

  sanitizePoints(value) {
    if (typeof value === 'number' && Number.isInteger(value) && value >= 0) {
      return value;
    }

    if (typeof value !== 'string' && typeof value !== 'number') return null;

    const text = String(value).trim();
    if (text === '') return null;
    if (!/^\d+$/.test(text)) return null;

    return Number.parseInt(text, 10);
  }

  onInputChange() {
    this.input.setCustomValidity('');

    const raw = this.input.value;
    if (raw === '') return;

    if (!/^\d+$/.test(raw)) {
      this.input.value = raw.replace(/[^\d]/g, '');
      this.setStatus('0以上の整数で入力してください', { isError: true });
      return;
    }

    const current = this.statusElement.textContent || '';
    if (
      this.balanceReady &&
      current &&
      !current.includes('上限') &&
      !current.includes('再適用') &&
      !current.includes('適用しました')
    ) {
      this.setStatus('');
    }
  }

  correctInputIfOverLimit() {
    if (!this.balanceReady || this.input.disabled) return;

    const usableLimit = this.getUsableLimit();
    const requestedPoints = this.sanitizePoints(this.input.value);

    if (requestedPoints === null) {
      this.input.value = String(this.appliedPoints);
      this.setStatus('0以上の整数で入力してください', { isError: true });
      return;
    }

    if (requestedPoints > usableLimit) {
      this.input.value = String(usableLimit);
      this.setStatus(
        `利用できる上限は${this.formatNumber(usableLimit)}ptです。上限値に修正しました。`
      );
    }
  }

  clampAppliedPoints() {
    const usableLimit = this.balanceReady ? this.getUsableLimit() : this.getMaxBySubtotal();
    this.input.max = String(Math.max(0, usableLimit));

    if (this.appliedPoints <= usableLimit) {
      if (document.activeElement !== this.input) {
        this.input.value = String(this.appliedPoints);
      }
      return false;
    }

    this.appliedPoints = usableLimit;
    this.input.value = String(usableLimit);
    if (this.attributeInput) this.attributeInput.value = String(usableLimit);
    return true;
  }

  async applyPoints() {
    if (!this.balanceReady || this.isApplying) return;

    let requestedPoints = this.sanitizePoints(this.input.value);
    const usableLimit = this.getUsableLimit();

    if (requestedPoints === null) {
      this.input.setCustomValidity('0以上の整数で入力してください');
      this.input.reportValidity();
      this.setStatus('0以上の整数で入力してください', { isError: true });
      return;
    }

    if (requestedPoints > usableLimit) {
      requestedPoints = usableLimit;
      this.input.value = String(usableLimit);
    }

    this.isApplying = true;
    this.setInputsEnabled(false);
    this.setStatus('ポイントを適用しています…');

    try {
      if (requestedPoints === 0) {
        await this.clearPointsApplication();
        this.needsReapply = false;
        this.reapplyMessage = '';
        this.setStatus('ポイント利用を解除しました');
        return;
      }

      let cart = await this.fetchCart();
      // 旧ポイント割引が残ったまま新コードを重ねないよう、先に解除する
      cart = await this.syncShopifyDiscounts(cart, { keepCode: '' });
      this.syncCartSnapshot(cart);

      const cartSubtotal = this.moneyToYen(cart.items_subtotal_price);
      const cartToken = cart.token;

      if (!cartToken) {
        throw new Error('Cart token is missing');
      }

      const response = await fetch('/apps/miyabi-api/points/apply', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        cache: 'no-store',
        body: JSON.stringify({
          requestedPoints,
          cartSubtotal,
          cartToken,
        }),
      });

      let payload = null;
      try {
        payload = await response.json();
      } catch (parseError) {
        throw new Error('Points apply API returned invalid JSON');
      }

      if (payload?.status === 'unauthorized' || response.status === 401) {
        this.handleUnauthorized(payload?.message);
        return;
      }

      if (!response.ok || payload?.status !== 'ok') {
        const message =
          payload?.message ||
          payload?.error ||
          `ポイントを適用できませんでした（${response.status}）`;
        throw new Error(message);
      }

      const appliedPoints = this.sanitizePoints(payload.appliedPoints);
      const discountCode = String(payload.discountCode || '').trim();
      const expiresAt = String(payload.expiresAt || '').trim();

      if (appliedPoints === null || appliedPoints <= 0 || !discountCode) {
        throw new Error('Points apply API returned invalid payload');
      }

      this.appliedPoints = appliedPoints;
      this.discountCode = discountCode;
      this.expiresAt = expiresAt;
      this.savedCartKey = this.buildCartKey(cart);
      this.input.value = String(appliedPoints);
      this.renderAppliedPoints();
      this.updateAdditionalCheckoutVisibility();

      await this.savePointAttributes({
        appliedPoints: String(appliedPoints),
        discountCode,
        expiresAt,
        cartKey: this.savedCartKey,
      });

      if (requestedPoints !== appliedPoints) {
        this.needsReapply = false;
        this.reapplyMessage = '';
        this.setStatus(
          `利用上限に合わせて${this.formatNumber(appliedPoints)}ptを適用しました`
        );
      } else {
        this.needsReapply = false;
        this.reapplyMessage = '';
        this.setStatus('ポイントを適用しました');
      }
    } catch (error) {
      this.setStatus(error?.message || 'ポイントを適用できませんでした', { isError: true });
      console.warn('Failed to apply points.', error);
    } finally {
      this.isApplying = false;
      if (this.balanceReady) this.setInputsEnabled(true);
    }
  }

  async clearPointsApplication() {
    this.clearLocalAppliedState();
    this.input.value = '0';
    this.renderAppliedPoints();
    this.updateAdditionalCheckoutVisibility();

    await this.savePointAttributes({
      appliedPoints: '',
      discountCode: '',
      expiresAt: '',
      cartKey: '',
    });

    await this.clearShopifyPointDiscounts();
  }

  async savePointAttributes({ appliedPoints, discountCode, expiresAt, cartKey }) {
    this.suppressCartInvalidate = true;

    try {
      const payload = {
        attributes: {
          [MIYABI_POINT_ATTR.appliedPoints]: appliedPoints,
          [MIYABI_POINT_ATTR.discountCode]: discountCode,
          [MIYABI_POINT_ATTR.expiresAt]: expiresAt,
          [MIYABI_POINT_ATTR.cartKey]: cartKey,
          [MIYABI_POINT_ATTR.legacyApplied]: appliedPoints,
        },
      };

      // 属性更新と同時に、画面上の最新メモも送って消えないようにする
      const noteField =
        document.getElementById('Cart-note') || document.getElementById('CartDrawer-Note');
      if (noteField) {
        payload.note = noteField.value;
      }

      const response = await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Cart update returned ${response.status}`);
      }

      return response.json();
    } finally {
      this.suppressCartInvalidate = false;
    }
  }

  updateAdditionalCheckoutVisibility() {
    const extraButtons = document.querySelector('.cart__dynamic-checkout-buttons');
    if (!extraButtons) return;
    extraButtons.hidden = this.hasValidDiscount();
  }

  renderAppliedPoints() {
    const discountYen = this.appliedPoints;
    const paymentYen = Math.max(0, this.getSubtotalYen() - discountYen);

    if (this.attributeInput) this.attributeInput.value = String(this.appliedPoints);
    if (this.discountCodeInput) this.discountCodeInput.value = this.discountCode || '';
    if (this.expiresAtInput) this.expiresAtInput.value = this.expiresAt || '';
    if (this.cartKeyInput) this.cartKeyInput.value = this.savedCartKey || '';
    this.discountElement.textContent = `-¥${this.formatNumber(discountYen)}`;
    this.paymentElement.textContent = `¥${this.formatNumber(paymentYen)}`;
  }

  formatNumber(value) {
    return new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 0 }).format(value);
  }
}

if (!customElements.get('cart-points')) {
  customElements.define('cart-points', CartPoints);
}
